import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import type { U2000AutoCompleteSearchResult } from "../u2000Core/types/U2000AutoCompleteSearchResult";
import { u2000ExtractAutoCompleteResults } from "../u2000Core/utils/u2000ExtractAutoCompleteResults";
import {
  doFindAvailablePeriods,
  doFindEmployee,
  doFindProject,
  doFindSubproject,
  doAssignTasksToProjectScope,
  doAssignTasksToSelectedProjects,
  doFindActiveTasks,
  doFindCommonTasksForProjects,
  doFindTasksForProject,
  doFindTasksNotAssignedToProject,
  luHandleApiError,
  type U3129AssignTasksToProjectScopeRequest,
  type U3129AssignTasksToProjectScopeResult,
  type U3129AssignTasksToSelectedProjectsRequest,
  type U3129AssignTasksToSelectedProjectsResult,
  type U3129FindActiveTasksRequest,
  type U3129FindCommonTasksForProjectsRequest,
  type U3129FindCommonTasksForProjectsResponse,
  type U3129FindAvailablePeriodsRequest,
  type U3129FindEmployeeItem,
  type U3129FindProjectItem,
  type U3129FindSubprojectItem,
  type U3129FindTasksForProjectRequest,
  type U3129TaskItem,
  type U3129AutoCompletCriterias,
} from "./U3129Service";
import { NS } from "./U3129";

export type U3129FindSearchCriterias = U3129AutoCompletCriterias;

/** Clé cache : même projet + même sous-projet (ou absence) = un seul appel réseau partagé entre les lignes honoraires. */
export function u3129BuildFindTasksForProjectCacheKey(body: U3129FindTasksForProjectRequest): string {
  const sub = body.subprojectId == null || String(body.subprojectId).trim() === "" ? "" : String(body.subprojectId).trim();
  return `${body.projectId.trim()}::${sub}`;
}

const findTasksForProjectResolvedCache = new Map<string, U3129TaskItem[]>();
const findTasksForProjectInflight = new Map<string, Promise<U3129TaskItem[]>>();
const findTasksNotAssignedResolvedCache = new Map<string, U3129TaskItem[]>();
const findTasksNotAssignedInflight = new Map<string, Promise<U3129TaskItem[]>>();

/** Invalide le cache findTasksForProject (et catalogue hors scope) pour une clé ou tout le cache. */
export function u3129InvalidateFindTasksForProjectCache(scopeKey?: string): void {
  if (scopeKey == null || scopeKey.trim() === "") {
    findTasksForProjectResolvedCache.clear();
    findTasksForProjectInflight.clear();
    findTasksNotAssignedResolvedCache.clear();
    findTasksNotAssignedInflight.clear();
    return;
  }
  findTasksForProjectResolvedCache.delete(scopeKey);
  findTasksForProjectInflight.delete(scopeKey);
  findTasksNotAssignedResolvedCache.delete(scopeKey);
  findTasksNotAssignedInflight.delete(scopeKey);
}

function parseU3129TaskListFromResponse(raw: unknown): U3129TaskItem[] {
  const results = Array.isArray(raw) ? raw : (raw as { results?: unknown })?.results;
  return Array.isArray(results) ? (results as U3129TaskItem[]) : [];
}

export function useU3129FindProjectHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3129FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<U3129FindProjectItem>> => {
      try {
        const response = await doFindProject(axios, criterias);
        return u2000ExtractAutoCompleteResults<U3129FindProjectItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast]
  );

  return { search };
}

export interface U3129FindSubprojectHandlersParams {
  getProjectId: () => string | undefined | null;
}

export function useU3129FindSubprojectHandlers(params: U3129FindSubprojectHandlersParams) {
  const { getProjectId } = params;
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3129FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<U3129FindSubprojectItem>> => {
      try {
        const projectId = getProjectId();
        const criteriasWithProject =
          projectId != null && projectId.trim() !== ""
            ? [...criterias, { fieldName: "projectId", value: projectId }]
            : criterias;
        const response = await doFindSubproject(axios, criteriasWithProject);
        return u2000ExtractAutoCompleteResults<U3129FindSubprojectItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast, getProjectId]
  );

  return { search };
}

export function useU3129FindEmployeeHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3129FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<U3129FindEmployeeItem>> => {
      try {
        const response = await doFindEmployee(axios, criterias);
        return u2000ExtractAutoCompleteResults<U3129FindEmployeeItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [axios, t, toast]
  );

  return { search };
}

export function useU3129FindAvailablePeriodsHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const fetchAvailablePeriods = useCallback(
    async (body: U3129FindAvailablePeriodsRequest): Promise<string[]> => {
      try {
        const response = await doFindAvailablePeriods(axios, body);
        const data = response.data;
        if (data == null) return [];
        const results = Array.isArray(data) ? data : (data as { results?: unknown }).results;
        if (!Array.isArray(results)) return [];
        const out: string[] = [];
        for (const item of results) {
          if (typeof item === "string") {
            out.push(item.slice(0, 10));
          } else if (item && typeof item === "object") {
            const o = item as Record<string, unknown>;
            const v = o.periodDate ?? o.value ?? o.period;
            if (typeof v === "string") {
              out.push(v.slice(0, 10));
            }
          }
        }
        return out.filter((s) => s.length === 10);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return [];
      }
    },
    [axios, t, toast]
  );

  return { fetchAvailablePeriods };
}

export type U3129FindTasksForProjectResult = { ok: true; tasks: U3129TaskItem[] } | { ok: false; tasks: U3129TaskItem[] };

export function useU3129FindTasksForProjectHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const fetchTasksResult = useCallback(
    async (body: U3129FindTasksForProjectRequest, options?: { silent?: boolean }): Promise<U3129FindTasksForProjectResult> => {
      const key = u3129BuildFindTasksForProjectCacheKey(body);
      const cached = findTasksForProjectResolvedCache.get(key);
      if (cached) {
        return { ok: true, tasks: cached };
      }
      const existing = findTasksForProjectInflight.get(key);
      if (existing) {
        try {
          const tasks = await existing;
          return { ok: true, tasks };
        } catch {
          return { ok: false, tasks: [] };
        }
      }

      const promise = (async (): Promise<U3129TaskItem[]> => {
        try {
          const response = await doFindTasksForProject(axios, body);
          const list = parseU3129TaskListFromResponse(response.data);
          findTasksForProjectResolvedCache.set(key, list);
          return list;
        } catch (error: unknown) {
          if (!options?.silent) {
            const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
            if (errorResult.genericErrors?.length) {
              errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
            }
          }
          throw error;
        } finally {
          findTasksForProjectInflight.delete(key);
        }
      })();

      findTasksForProjectInflight.set(key, promise);
      try {
        const tasks = await promise;
        return { ok: true, tasks };
      } catch {
        return { ok: false, tasks: [] };
      }
    },
    [axios, t, toast],
  );

  const fetchTasks = useCallback(
    async (body: U3129FindTasksForProjectRequest): Promise<U3129TaskItem[]> => {
      const result = await fetchTasksResult(body);
      return result.tasks;
    },
    [fetchTasksResult],
  );

  const fetchTasksNotAssigned = useCallback(
    async (body: U3129FindTasksForProjectRequest): Promise<U3129TaskItem[]> => {
      const key = u3129BuildFindTasksForProjectCacheKey(body);
      const cached = findTasksNotAssignedResolvedCache.get(key);
      if (cached) {
        return cached;
      }
      const existing = findTasksNotAssignedInflight.get(key);
      if (existing) {
        return existing;
      }

      const promise = (async (): Promise<U3129TaskItem[]> => {
        try {
          const response = await doFindTasksNotAssignedToProject(axios, body);
          const list = parseU3129TaskListFromResponse(response.data);
          findTasksNotAssignedResolvedCache.set(key, list);
          return list;
        } catch (error: unknown) {
          const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
          if (errorResult.genericErrors?.length) {
            errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
          }
          return [];
        } finally {
          findTasksNotAssignedInflight.delete(key);
        }
      })();

      findTasksNotAssignedInflight.set(key, promise);
      return promise;
    },
    [axios, t, toast]
  );

  const assignTasksToProjectScope = useCallback(
    async (body: U3129AssignTasksToProjectScopeRequest): Promise<U3129AssignTasksToProjectScopeResult | null> => {
      try {
        const response = await doAssignTasksToProjectScope(axios, body);
        const raw = response.data as { result?: U3129AssignTasksToProjectScopeResult } | U3129AssignTasksToProjectScopeResult;
        const result =
          raw != null && typeof raw === "object" && "result" in raw && raw.result != null
            ? raw.result
            : (raw as U3129AssignTasksToProjectScopeResult);
        if (result && typeof result.assignedCount === "number") {
          return result;
        }
        return { assignedCount: body.taskIds.length };
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      }
    },
    [axios, t, toast]
  );

  const fetchCommonTasksForProjects = useCallback(
    async (body: U3129FindCommonTasksForProjectsRequest): Promise<U3129FindCommonTasksForProjectsResponse | null> => {
      try {
        const response = await doFindCommonTasksForProjects(axios, body);
        const raw = response.data as
          | U3129FindCommonTasksForProjectsResponse
          | { result?: U3129FindCommonTasksForProjectsResponse };
        const data =
          raw != null && typeof raw === "object" && "result" in raw && raw.result != null
            ? raw.result
            : (raw as U3129FindCommonTasksForProjectsResponse);
        const results = Array.isArray(data?.results) ? data.results : [];
        const byProject = Array.isArray(data?.byProject) ? data.byProject : [];
        return { results, byProject };
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      }
    },
    [axios, t, toast],
  );

  const fetchActiveTasks = useCallback(
    async (body: U3129FindActiveTasksRequest = {}): Promise<U3129TaskItem[]> => {
      try {
        const response = await doFindActiveTasks(axios, body);
        return parseU3129TaskListFromResponse(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return [];
      }
    },
    [axios, t, toast],
  );

  const assignTasksToSelectedProjects = useCallback(
    async (body: U3129AssignTasksToSelectedProjectsRequest): Promise<U3129AssignTasksToSelectedProjectsResult | null> => {
      try {
        const response = await doAssignTasksToSelectedProjects(axios, body);
        const raw = response.data as
          | { result?: U3129AssignTasksToSelectedProjectsResult }
          | U3129AssignTasksToSelectedProjectsResult;
        const result =
          raw != null && typeof raw === "object" && "result" in raw && raw.result != null
            ? raw.result
            : (raw as U3129AssignTasksToSelectedProjectsResult);
        if (result && typeof result.assignedCount === "number") {
          return {
            assignedCount: result.assignedCount,
            byProject: Array.isArray(result.byProject) ? result.byProject : [],
          };
        }
        return { assignedCount: body.taskIds.length, byProject: [] };
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      }
    },
    [axios, t, toast],
  );

  return {
    fetchTasks,
    fetchTasksResult,
    fetchTasksNotAssigned,
    assignTasksToProjectScope,
    fetchCommonTasksForProjects,
    fetchActiveTasks,
    assignTasksToSelectedProjects,
  };
}
